import { api } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import {
    PriceCodeParams,
    PriceCodeResponse,
    PriceExtraCodeParams,
    PriceExtraCodeResponse,
    PriceRequestCode,
    PriceRequestExtraCode,
    PriceUnitAndExtraRequest,
    PriceLookupParams,
    PriceLookupByCodeParams,
    PriceLookupResponse,
} from "@/interface/standard/st_price_code/index";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export const usePriceCode = (params: PriceCodeParams, options?: { enabled?: boolean }
) => {
    const { stPriceUnitCode } = params;
    return useQuery<PriceCodeResponse>({
        queryKey: ["priceCode", stPriceUnitCode],
        queryFn: async () => {
            // stPriceUnitCode가 없으면 요청하지 않음
            if (!stPriceUnitCode) {
                throw new Error("stPriceUnitCode is required");
            }
            // 경로 변수로만 전달, body는 비움 (또는 쿼리 파라미터로 pageable 전달)
            const response = await api.get(`/price-code/${stPriceUnitCode}`, { params });
            return response.data;
        },
        enabled: options?.enabled ?? (!!stPriceUnitCode),
    });
};

export const usePriceExtraCode = (params: PriceExtraCodeParams, options?: { enabled?: boolean }) => {
    const { stPriceExtraCode } = params;
    return useQuery<PriceExtraCodeResponse>({
        queryKey: ["priceExtraCode", stPriceExtraCode],
        queryFn: async () => {
            const response = await api.get(`/price-code/${stPriceExtraCode}/extras`, { params });
            return response.data;
        },
        enabled: options?.enabled ?? (!!stPriceExtraCode),
    });
};

export const usePriceCodeInsert = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: PriceRequestCode[] | PriceRequestCode) => {
            const dataArray = Array.isArray(data) ? data : [data];
            // 배열 전체를 한 번에 전송
            const response = await api.post("/price-code/unit-codes", dataArray);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceCode"] });
            toast.success(`단가비가 추가되었습니다.`);
        },
        onError: (error: any) => {
            // 백엔드 응답에서 예외 메시지 추출
            const errorMessage = error?.response?.data?.message;
            if (errorMessage) {
                console.log(errorMessage);
            } 
        },
    });
};

export const usePriceUnitAndExtraInsert = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: PriceUnitAndExtraRequest) => {
            const response = await api.post("/price-code/unit-and-extra", data);
            return response.data;
        },
        onSuccess: () => {
            // 단가비 / 추가비 둘 다 다시 조회
            queryClient.invalidateQueries({ queryKey: ["priceCode"] });
            queryClient.invalidateQueries({ queryKey: ["priceExtraCode"] });
            toast.success("단가비/추가비가 저장되었습니다.");
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message;
            if (errorMessage) {
                toast.error(errorMessage);
            }
        },
    });
};

export const usePriceCodeUpdate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: PriceRequestCode[] | PriceRequestCode) => {
            const dataArray = Array.isArray(data) ? data : [data];
            const response = await api.put("/price-code/unit-codes", dataArray);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceCode"] });
            toast.success(`단가비가 수정되었습니다.`);
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message;
            if (errorMessage) {
                toast.error(errorMessage);
            }
        },
    });
};

export const usePriceExtraCodeUpdate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: PriceRequestExtraCode) => {
            const response = await api.put(`/price-code/extra-codes/${data.stPriceExtraCode}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceExtraCode"] });
            toast.success(`추가비용이 수정되었습니다.`);
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message;
            if (errorMessage) {
                toast.error(errorMessage);
            }
        },
    });
};

export const usePriceUnitAndExtraUpdate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: PriceUnitAndExtraRequest) => {
            const response = await api.put("/price-code/unit-and-extra", data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceCode"] });
            queryClient.invalidateQueries({ queryKey: ["priceExtraCode"] });
            toast.success("단가비/추가비가 수정되었습니다.");
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message;
            if (errorMessage) {
                toast.error(errorMessage);
            }
        },
    });
};

export const usePriceCodeDelete = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params: { stPriceUnitCode: number; stDist: string }) => {
            const { stPriceUnitCode, stDist } = params;
            const response = await api.delete(
                `/price-code/unit-codes/${stPriceUnitCode}`,
                { params: { stDist } }
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceCode"] });
            toast.success(`단가비가 삭제되었습니다.`);
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "단가비 삭제에 실패했습니다.");
        },
    });
};

export const usePriceExtraCodeDelete = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params: { stPriceExtraCode: number; stParentCode: number }) => {
            const { stPriceExtraCode, stParentCode } = params;
            const response = await api.delete(
                `/price-code/extra-codes/${stPriceExtraCode}`,
                { params: { stParentCode } }
            );
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceExtraCode"] });
            toast.success(`단가비가 삭제되었습니다.`);
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "단가비 삭제에 실패했습니다.");
        },
    });
};

// 매출처명과 지역으로 단가 조회
export const usePriceLookup = (params: PriceLookupParams, options?: { enabled?: boolean }) => {
    const { companyName, region, weight } = params;
    return useQuery<PriceLookupResponse | null>({
        queryKey: ["priceLookup", companyName, region, weight],
        queryFn: async () => {
            if (!companyName || !region) {
                return null;
            }
            const response = await api.get("/price-code/lookup", {
                params: { companyName, region, weight }
            });
            return response.data;
        },
        enabled: options?.enabled ?? (!!companyName && !!region),
    });
};

// 단가코드 ID와 지역으로 단가 조회
export const usePriceLookupByCode = (params: PriceLookupByCodeParams, options?: { enabled?: boolean }) => {
    const { priceCodeId, region, weight } = params;
    return useQuery<PriceLookupResponse | null>({
        queryKey: ["priceLookupByCode", priceCodeId, region, weight],
        queryFn: async () => {
            if (!priceCodeId || !region) {
                return null;
            }
            const response = await api.get("/price-code/lookup-by-code", {
                params: { priceCodeId, region, weight }
            });
            return response.data;
        },
        enabled: options?.enabled ?? (!!priceCodeId && !!region),
    });
};

// 단가 일괄 조정 (% 적용)
export interface AdjustPriceParams {
    companyCodeId: number;
    percentage: number;
}

export const useAdjustPrice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params: AdjustPriceParams) => {
            const response = await api.post("/price-code/adjust-price", params);
            return response.data;
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ["priceCode"] });
            toast.success(data || "단가가 조정되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "단가 조정에 실패했습니다.");
        },
    });
};

// 회사별 퍼센테이지 조회
export const usePricePercentage = (companyCodeId: number | undefined, options?: { enabled?: boolean }) => {
    return useQuery<number>({
        queryKey: ["pricePercentage", companyCodeId],
        queryFn: async () => {
            if (!companyCodeId) return 0;
            const response = await api.get(`/price-code/percentage/${companyCodeId}`);
            return response.data ?? 0;
        },
        enabled: options?.enabled ?? !!companyCodeId,
    });
};

// 회사별 퍼센테이지 저장
export interface SavePercentageParams {
    companyCodeId: number;
    percentage: number;
    modifiedBy: string;
}

export const useSavePercentage = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (params: SavePercentageParams) => {
            const response = await api.post("/price-code/percentage", params);
            return response.data;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["pricePercentage", variables.companyCodeId] });
            toast.success("퍼센테이지가 저장되었습니다.");
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || "퍼센테이지 저장에 실패했습니다.");
        },
    });
};